import React from "react";
import Navbar from "../components/layout/Navbar";
import Reveal from "../components/common/Reveal";
import Footer from "../components/layout/Footer";
import { BLOG_POSTS, BlogPost } from "../data/blogs";

const getBlogById = (id: string): BlogPost | null => {
  return BLOG_POSTS.find((blog) => blog.id === id) || null;
}

const BlogDetailPage: React.FC = () => {
  const hash = window.location.hash; 
  const params = new URLSearchParams(hash.split('?')[1]);
  const blogId = params.get("id") || "";

  const blog = getBlogById(blogId);

  if (!blog) {
    return (
      <div className="min-h-screen bg-primary">
        <Navbar />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-accentNavy mb-4">Blog Not Found</h1>
            <a href="#blogs" className="text-accentGold hover:underline">
              Back to Blogs
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-accentGold/5 to-accentNavy/10 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accentNavy/20 via-transparent to-accentGold/15" />
        <div className="absolute -right-32 top-1/2 h-96 w-96 rounded-full bg-accentGold/30 blur-3xl" />
        <div className="absolute -left-32 bottom-1/4 h-96 w-96 rounded-full bg-accentNavy/20 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <a
              href="#blogs"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-accentNavy/70 hover:text-accentGold transition-colors duration-200"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blogs
            </a>
          </Reveal>

          <Reveal delay={120}>
            <div className="mb-6 flex items-center gap-3">
              <span className="rounded-full border border-accentNavy/20 bg-accentNavy/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-accentNavy/70">
                {blog.category}
              </span>
              <span className="text-sm text-accentNavy/50">{blog.readTime}</span>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <h1 className="mb-6 font-display text-4xl sm:text-5xl lg:text-6xl text-accentNavy leading-tight">
              {blog.title}
            </h1>
          </Reveal>

          {/* <Reveal delay={220}>
            <div className="flex items-center gap-4 border-b border-accentNavy/10 pb-8">
              <div>
                <p className="text-sm font-semibold text-accentNavy">{blog.author}</p>
                <p className="text-sm text-accentNavy/50">
                  {new Date(blog.date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </Reveal> */}
        </div>
      </section>

      {/* Content Section */}
      <section className="relative bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          {blog.image && (
            <Reveal>
              <div className="mb-12 overflow-hidden rounded-3xl">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </Reveal>
          )}

          <Reveal delay={120}>
            <div className="prose prose-lg max-w-none">
              <div className="space-y-6 text-base sm:text-lg text-accentNavy/80 leading-relaxed whitespace-pre-line">
                {blog.content.split("\n\n").map((paragraph, index) => {
                  if (paragraph.startsWith("## ")) {
                    return (
                      <h2 key={index} className="mt-8 mb-4 text-2xl font-semibold text-accentNavy">
                        {paragraph.replace("## ", "")}
                      </h2>
                    );
                  }
                  if (paragraph.startsWith("### ")) {
                    return (
                      <h3 key={index} className="mt-6 mb-3 text-xl font-semibold text-accentNavy">
                        {paragraph.replace("### ", "")}
                      </h3>
                    );
                  }
                  if (paragraph.startsWith("1. ") || paragraph.startsWith("- ")) {
                    const items = paragraph.split(/\d+\. |^- /).filter(Boolean);
                    return (
                      <ul key={index} className="ml-6 list-disc space-y-2">
                        {items.map((item, idx) => (
                          <li key={idx} className="text-accentNavy/80">
                            {item.trim()}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={index} className="text-accentNavy/80">
                      {paragraph}
                    </p>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <div className="mt-16 flex items-center justify-between border-t border-accentNavy/10 pt-8">
              <a
                href="#blogs"
                className="inline-flex items-center gap-2 rounded-full border border-accentNavy/20 bg-white/80 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-accentNavy transition-all duration-300 hover:border-accentGold hover:bg-accentGold hover:text-white"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Blogs
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;

